export type Network = "tron" | "ethereum" | "polygon" | "solana";
export const NETWORKS: Network[] = ["tron", "ethereum", "polygon", "solana"];

const BASE58 = /^[1-9A-HJ-NP-Za-km-z]+$/;

export function isValidAddress(network: Network, address: string): boolean {
  const a = address.trim();
  switch (network) {
    case "tron":
      return a.length === 34 && a.startsWith("T") && BASE58.test(a);
    case "ethereum":
    case "polygon":
      return /^0x[0-9a-fA-F]{40}$/.test(a);
    case "solana":
      return a.length >= 32 && a.length <= 44 && BASE58.test(a);
  }
}

export function isValidCNPJ(input: string): boolean {
  const c = input.replace(/\D/g, "");
  if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false;
  const calc = (len: number) => {
    let sum = 0;
    let pos = len - 7;
    for (let i = len; i >= 1; i--) {
      sum += Number(c[len - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === Number(c[12]) && calc(13) === Number(c[13]);
}

export function isValidCPF(input: string): boolean {
  const c = input.replace(/\D/g, "");
  if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  const d = (n: number) => {
    let s = 0;
    for (let i = 0; i < n; i++) s += Number(c[i]) * (n + 1 - i);
    const r = (s * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return d(9) === Number(c[9]) && d(10) === Number(c[10]);
}

export function isValidEmail(e: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
}

/** Routing number (ABA) americano: 9 dígitos com dígito verificador (soma ponderada 3-7-1 múltipla de 10). */
export function isValidRoutingNumber(input: string): boolean {
  const d = input.replace(/\s+/g, "");
  if (!/^\d{9}$/.test(d)) return false;
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  const sum = [...d].reduce((acc, ch, i) => acc + Number(ch) * w[i]!, 0);
  return sum % 10 === 0;
}

/** BIC / SWIFT: 8 ou 11 caracteres (banco 4 letras, país 2 letras, localidade 2, agência 3 opcional). */
export function isValidBic(input: string): boolean {
  return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(input.replace(/\s+/g, "").toUpperCase());
}

/** IBAN: forma (país + 2 dígitos + 11 a 30 alfanuméricos) e dígitos verificadores mod 97. */
export function isValidIban(input: string): boolean {
  const v = input.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(v)) return false;
  const rearranged = v.slice(4) + v.slice(0, 4);
  let rem = 0;
  for (const ch of rearranged) {
    const piece = /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of piece) rem = (rem * 10 + Number(digit)) % 97;
  }
  return rem === 1;
}
